import EventTracking from 'lib/EventTracking';
import { dispatcher } from 'lib/Dispatcher';
import { ActionTypes } from 'constants/AppConstants';
import { PushEvents } from 'pusher/PusherConstants';

/**
 * This lib is to time the duration between a user action and the UI updating to satisfy the user.
 *
 * 1. markNewTransition() At start of user action.
 * 2. markChange() At each point that the UI may update to satisfy that action.
 *
 * We use this "flush" technique because there's no way to know the moment
 * that the action completes. For example, on navigating to a new chat we know
 * markNewTransition when they click to it and markChange when the new chat renders.
 * At this point we don't know whether it's done or not because we have to
 * wait for an async request to complete. When it does, we markChanged()
 * again. In theory there could be more changes, so we take the latest time
 * with and run that when a new transition starts.
 *
 * Note that if agent's system clock changes between start and end there will
 * be bad data. Rare in practice, probably dominated by other noise. We could
 * have window.performance.marks against the browser's own clock, but it's not
 * supported in Safari and it parallel timers would still have to be added on
 * top. Plus its microsecond resolution is overkill as yet.
 */

// If not we fall back to module load time. Not as accurate but we don't
// officially support any agents that don't have Navigation Timing API so the
// data points should be few.
const hasNavigationTiming =
  typeof window !== 'undefined' && window.performance && window.performance.timing;

let startTime: number = hasNavigationTiming
  ? window.performance.timing.navigationStart
  : Date.now();

let lastChanges: { [key: string]: string } = {};

let endTimes: { [key: string]: number } = {};

let appMounted = false;

function resetTimes() {
  startTime = Date.now();
  lastChanges = {};
  endTimes = {};
}

export const flushTime = (transitionKey: string): void => {
  const endTime = endTimes[transitionKey];

  const duration = endTime - startTime;
  const props = {
    duration,
    last_change: lastChanges[transitionKey],
  };

  EventTracking.track('ui.timing.' + transitionKey, props);

  // console.debug('flushTime',
  //   Object.assign({ transitionKey, startTime, endTimes, lastChanges }, props));
};

/**
 * Marks the start of a new transition, clearing the buffer of the previous.
 * Ideally place in a route onEnter. If the transition doesn't have a route,
 * it can be added elsewhere but it's probably better to make a route if this
 * transition is key enough to track.
 */
export const markNewTransition = function (_pathname: string): void {
  if (!appMounted) {
    // ignore because it must be a redirect, not a user initiated transition
    return;
  }

  // console.debug('markNewTransition flushing', pathname, Object.keys(endTimes));

  for (const transitionKey in endTimes) {
    flushTime(transitionKey);
  }

  resetTimes();
};

/**
 * The view has changed. Marks a possible end of the transition. The latest
 * change is considered the "end" of the transition for the UX.
 */
export const markChange = function (transitionKey: string, change: string): void {
  // console.debug('markChange', transitionKey, change);
  endTimes[transitionKey] = Date.now();
  lastChanges[transitionKey] = change;
};

export const markAppMounted = (): void => {
  appMounted = true;
  markChange('app_mount', 'mounted');
  if (hasNavigationTiming) {
    endTimes.domComplete = window.performance.timing.domComplete;
    lastChanges.domComplete = 'domComplete';
  }
};

type DispatchAction = {
  type:
    | typeof ActionTypes.CONNECTION_STATE_CHANGED
    | typeof PushEvents.CHANGESET_CREATE_CHAT_MESSAGE
    | typeof PushEvents.CLASS_MEMBERSHIP;
  current: string;
};

function handleDispatch(action: DispatchAction): void {
  switch (action.type) {
    case ActionTypes.CONNECTION_STATE_CHANGED:
      if (action.current !== 'connected') {
        resetTimes();
      }
      break;
    case PushEvents.CHANGESET_CREATE_CHAT_MESSAGE:
    case PushEvents.CLASS_MEMBERSHIP:
      // Could trigger new data load that wasn't user initiated so if we don't
      // reset then user wait timers would report new data arriving very much
      // later than user action. Ideally we'd track open XHRs to know whether
      // data is really pending. Boomerang could help us with this, but it'll
      // be pretty complicated regardless. This works for now.
      markNewTransition(`PushEvent ${action.type}`);
      break;
    default:
      break;
  }
}

// at module load
dispatcher.register(handleDispatch);

export default {
  markChange,
  markAppMounted,
  markNewTransition,
  flushTime,
};
